import { memo, type MouseEvent, useEffect, useRef, useState } from 'react';

import type { Question } from '@/api';
import { prefersReducedMotion } from '@/hooks';
import { plural, type Strings } from '@/str';

interface JumpStripProps {
  questions: readonly (Question | undefined)[];
  index: number;
  answers: Record<number, number>;
  onJump: (i: number) => void;
  t: Strings;
  label?: string;
}

type Done = 'ok' | 'bad' | undefined;

const CELL = 30;
const GAP = 4;
const STEP = CELL + GAP;
const REACH = 30;

const JUMP_ROW =
  'no-scrollbar flex snap-x snap-proximity gap-1 overflow-x-auto overflow-y-hidden -my-2 pt-2.25 pb-2.5';

const JUMP_CELL =
  "relative h-8 flex-[0_0_30px] snap-center rounded-xs border border-border bg-card p-0 text-t11h font-extrabold tabular-nums text-faint after:absolute after:inset-x-0 after:-inset-y-2 after:content-[''] data-[done=ok]:border-primary data-[done=ok]:bg-primary data-[done=ok]:text-primary-foreground data-[done=bad]:border-bad data-[done=bad]:bg-bad data-[done=bad]:text-on-acc data-[now]:border-2 data-[now]:border-primary data-[now]:text-accent-ink data-[now]:data-[done]:text-primary-foreground data-[now]:data-[done=bad]:text-on-acc";

const JumpCell = memo(function JumpCell({ i, done, now }: { i: number; done: Done; now: boolean }) {
  return (
    <button
      type="button"
      data-i={i}
      data-done={done}
      data-now={now ? '' : undefined}
      className={JUMP_CELL}
      aria-current={now ? 'step' : undefined}
    >
      {i + 1}
    </button>
  );
});

function doneOf(question: Question | undefined, picked: number | undefined): Done {
  if (picked === undefined) return undefined;
  const correct = question?.answers.find((a) => a.answer_index === picked)?.is_correct ?? false;
  return correct ? 'ok' : 'bad';
}

export function JumpStrip({ questions, index, answers, onJump, t, label }: JumpStripProps) {
  const row = useRef<HTMLDivElement>(null);
  const settled = useRef(false);
  const frame = useRef(0);
  const [scrolled, setScrolled] = useState<{ index: number; at: number } | null>(null);

  const count = questions.length;
  const center = scrolled?.index === index ? scrolled.at : index;
  const from = Math.max(0, center - REACH);
  const to = Math.min(count, center + REACH + 1);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const left = index * STEP - el.clientWidth / 2 + CELL / 2;
    el.scrollTo({
      left: Math.max(0, left),
      behavior: settled.current && !prefersReducedMotion() ? 'smooth' : 'auto',
    });
    settled.current = true;
  }, [index]);

  const follow = () => {
    if (frame.current !== 0) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const el = row.current;
      if (!el) return;
      const at = Math.round((el.scrollLeft + el.clientWidth / 2 - CELL / 2) / STEP);
      const clamped = Math.min(count - 1, Math.max(0, at));
      if (Math.abs(clamped - center) > REACH / 3) setScrolled({ index, at: clamped });
    });
  };

  const pick = (e: MouseEvent<HTMLDivElement>) => {
    const at = (e.target as Element).closest<HTMLElement>('[data-i]')?.dataset.i;
    if (at !== undefined) onJump(Number(at));
  };

  return (
    <div className="flex flex-col gap-1.25">
      {label !== undefined && (
        <div className="flex items-baseline justify-between gap-2.5">
          <span className="text-t11 text-faint tracking-kicker font-bold whitespace-nowrap uppercase">
            {label}
          </span>
          <span
            data-jump-count
            className="text-t10h text-faint font-bold whitespace-nowrap tabular-nums"
          >
            {Object.keys(answers).length} / {questions.length}{' '}
            {plural(questions.length, t.savolUnit)}
          </span>
        </div>
      )}
      <div className={JUMP_ROW} ref={row} onClick={pick} onScroll={follow}>
        {from > 0 && (
          <div
            aria-hidden
            data-strip-gap
            className="flex-none"
            style={{ width: from * STEP - GAP }}
          />
        )}
        {questions.slice(from, to).map((q, k) => (
          <JumpCell
            key={from + k}
            i={from + k}
            done={doneOf(q, answers[from + k])}
            now={from + k === index}
          />
        ))}
        {to < count && (
          <div
            aria-hidden
            data-strip-gap
            className="flex-none"
            style={{ width: (count - to) * STEP - GAP }}
          />
        )}
      </div>
    </div>
  );
}
